<template>
    <DataTable :value="users" showGridlines lazy paginator :rows="rows" :totalRecords="totalRecords" :first="first"
        :loading="loading" :filters="filters" filterDisplay="menu" scrollable scrollHeight="calc(100vh - 16rem)"
        :sortField="sortField" :sortOrder="sortOrder"
        :globalFilterFields="['username', 'firstName', 'lastName', 'email']" @page="$emit('page', $event)"
        @filter="$emit('filter', $event)" @sort="$emit('sort', $event)">
        <template #loading></template>
        <template #empty>
            <div class="text-center py-12 text-gray-500">
                <i class="fa-solid fa-users text-3xl mb-3"></i>
                <p class="font-semibold">{{ t('userGroups.noUsersFound') }}</p>
            </div>
        </template>

        <template #header>
            <div class="flex justify-end items-center">
                <Button icon="fa-solid fa-filter-circle-xmark" :label="t('clearFilters')"
                    @click="$emit('clearFilters')" />
            </div>
        </template>

        <!-- USERNAME -->
        <Column field="username" :header="t('users.username')" sortable filter
            :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <span v-else>{{ data.username }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('users.username')" />
            </template>
        </Column>

        <!-- FIRST NAME -->
        <Column field="firstName" :header="t('users.firstName')" sortable filter
            :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <span v-else>{{ data.firstName }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('users.firstName')" />
            </template>
        </Column>

        <!-- LAST NAME -->
        <Column field="lastName" :header="t('users.lastName')" sortable filter
            :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <span v-else>{{ data.lastName }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('users.lastName')" />
            </template>
        </Column>

        <!-- EMAIL -->
        <Column field="email" :header="t('users.email')" sortable filter :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="150px" height="1rem" />
                <span v-else>{{ data.email }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('users.email')" />
            </template>
        </Column>

        <!-- ROLE -->
        <Column field="role" :header="t('users.role')" sortable filter :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <span v-else>{{ data.role?.name ?? '-' }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('users.role')" />
            </template>
        </Column>

        <!-- STATUS -->
        <Column field="status" :header="t('status.title')" sortable filter
            :filterMatchModeOptions="selectMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <Tag v-else :value="data.enabled ? t('common.enabled') : t('common.disabled')"
                    :severity="data.enabled ? 'success' : 'danger'" />
            </template>
            <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="statusOptions" optionLabel="label" />
            </template>
        </Column>

        <!-- ACTIONS -->
        <Column headerStyle="width:6rem" :header="t('actions')">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <div v-else class="flex gap-1">
                    <Button v-if="data.editable" icon="fa-solid fa-pen-to-square" class="p-button-text p-button-rounded"
                        severity="primary" v-tooltip.top="t('users.edit')" @click="$emit('edit', data)" />
                </div>
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import { useFilterMatchModeOptions } from '@/composables/useFilterMatchModeOptions';
import type { User } from '@/lib/types/user';
import type { DataTableFilterEvent, DataTableSortEvent } from 'primevue/datatable';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const {
    textMatchModeOptions,
    selectMatchModeOptions
} = useFilterMatchModeOptions();

interface Props {
    users: User[];
    loading: boolean;
    filters: any;
    sortField?: string;
    sortOrder: number;
    first: number;
    rows: number;
    totalRecords: number;
}

defineProps<Props>();

defineEmits<{
    page: [event: any];
    filter: [event: DataTableFilterEvent];
    sort: [event: DataTableSortEvent];
    clearFilters: [];
    edit: [user: User];
}>();

const statusOptions = computed(() => [
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);
</script>
